import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SignatureCapture = ({ document, onSign, onCancel }) => {
  const [signatureType, setSignatureType] = useState('typed'); // typed, drawn, upload
  const [typedSignature, setTypedSignature] = useState('');
  const [signatureFont, setSignatureFont] = useState('cursive');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const [drawnSignature, setDrawnSignature] = useState(null);

  const fontOptions = [
    { value: 'cursive', label: 'Cursive', style: 'font-cursive' },
    { value: 'script', label: 'Script', style: 'font-script' },
    { value: 'elegant', label: 'Elegant', style: 'font-elegant' }
  ];

  const handleTypedSignatureChange = (e) => {
    setTypedSignature(e?.target?.value);
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const y = e?.clientY - rect?.top;
    
    const ctx = canvas?.getContext('2d');
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const y = e?.clientY - rect?.top;
    
    const ctx = canvas?.getContext('2d');
    ctx?.lineTo(x, y);
    ctx?.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef?.current;
      setDrawnSignature(canvas?.toDataURL());
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setDrawnSignature(null);
  };

  const handleSign = () => {
    let signatureData = null;
    
    switch (signatureType) {
      case 'typed':
        if (typedSignature?.trim()) {
          signatureData = {
            type: 'typed',
            text: typedSignature,
            font: signatureFont
          };
        }
        break;
      case 'drawn':
        if (drawnSignature) {
          signatureData = {
            type: 'drawn',
            image: drawnSignature
          };
        }
        break;
    }

    if (signatureData) {
      onSign(document?.id, {
        ...signatureData,
        timestamp: new Date()?.toISOString(),
        ipAddress: '192.168.1.100', // Mock IP
        userAgent: navigator.userAgent
      });
    }
  };

  const canSubmit = () => {
    switch (signatureType) {
      case 'typed':
        return typedSignature?.trim()?.length > 0;
      case 'drawn':
        return drawnSignature !== null;
      default:
        return false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="PenTool" size={24} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Digital Signature
              </h2>
              <p className="text-sm text-muted-foreground">
                Sign: {document?.title}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onCancel}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Signature Type Selection */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">
              Signature Method
            </h3>
            <div className="flex space-x-4">
              <button
                onClick={() => setSignatureType('typed')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border nav-transition ${
                  signatureType === 'typed' ?'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Type" size={16} />
                <span className="text-sm">Type</span>
              </button>
              <button
                onClick={() => setSignatureType('drawn')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border nav-transition ${
                  signatureType === 'drawn' ?'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="PenTool" size={16} />
                <span className="text-sm">Draw</span>
              </button>
            </div>
          </div>

          {/* Typed Signature */}
          {signatureType === 'typed' && (
            <div className="space-y-4">
              <Input
                label="Type your full name"
                type="text"
                placeholder="Enter your full name"
                value={typedSignature}
                onChange={handleTypedSignatureChange}
              />
              
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Font Style
                </label>
                <div className="flex space-x-3">
                  {fontOptions?.map((font) => (
                    <button
                      key={font?.value}
                      onClick={() => setSignatureFont(font?.value)}
                      className={`px-4 py-2 rounded-lg border nav-transition ${
                        signatureFont === font?.value
                          ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <span className={`text-lg ${font?.style}`}>
                        {font?.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Signature Preview */}
              {typedSignature && (
                <div className="p-4 border border-border rounded-lg bg-muted/20">
                  <p className="text-sm text-muted-foreground mb-2">Preview:</p>
                  <div 
                    className={`text-2xl text-foreground ${
                      signatureFont === 'cursive' ? 'font-cursive' :
                      signatureFont === 'script' ? 'font-script' : 'font-elegant'
                    }`}
                  >
                    {typedSignature}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Drawn Signature */}
          {signatureType === 'drawn' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Draw your signature
                </label>
                <div className="border border-border rounded-lg bg-white">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={200}
                    className="w-full h-48 cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="RotateCcw"
                    onClick={clearCanvas}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Legal Notice */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="Shield" size={16} className="text-primary mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Legal Agreement</p>
                <p>
                  By signing this document, you acknowledge that you have read, understood, and agree to be bound by its terms. 
                  This electronic signature has the same legal effect as a handwritten signature.
                </p>
              </div>
            </div>
          </div>

          {/* Signature Details */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/20 rounded-lg">
            <div>
              <p className="text-xs text-muted-foreground">Signing as</p>
              <p className="text-sm font-medium text-foreground">Solution Architect</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Date & Time</p>
              <p className="text-sm font-medium text-foreground">
                {new Date()?.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">IP Address</p>
              <p className="text-sm font-medium text-foreground">192.168.1.100</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Authentication</p>
              <p className="text-sm font-medium text-foreground">Multi-factor verified</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Lock" size={16} />
            <span>256-bit SSL encrypted</span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleSign}
              disabled={!canSubmit()}
            >
              Sign Document
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureCapture;